import { useMemo, useState } from 'react';
import { Plus, Search, LayoutList, Columns3, Building2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import { useClients, useDeleteClient } from '@/hooks/useClients';
import {
  CLIENT_TYPE_LABELS, PIPELINE_LABELS, PIPELINE_STAGES, ACTIVE_STAGES,
  type Client, type ClientType, type PipelineStage,
} from '@/types/clients';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Card, SectionHeader } from '@/components/ui/Card';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { formatCurrency } from '@/lib/utils';
import { ClientFormModal } from '@/components/clients/ClientFormModal';
import { ClientsTableView } from '@/components/clients/ClientsTableView';
import { PipelineView } from '@/components/clients/PipelineView';

type View = 'list' | 'pipeline';

export function ClientsListPage() {
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'clients', 'write') : false;

  const [view, setView] = useState<View>('list');
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState<'' | PipelineStage>('');
  const [type, setType] = useState<'' | ClientType>('');

  const { data, isLoading, error } = useClients();
  const deleteClient = useDeleteClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);
  const [deleting, setDeleting] = useState<Client | null>(null);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter((c) => {
      if (stage && c.stage !== stage) return false;
      if (type && c.type !== type) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${c.name} ${c.email ?? ''} ${c.territory ?? ''} ${c.notes ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, search, stage, type]);

  const stats = useMemo(() => {
    if (!data) return { total: 0, active: 0, won: 0, pipelineValue: 0 };
    return {
      total: data.length,
      active: data.filter((c) => ACTIVE_STAGES.includes(c.stage)).length,
      won: data.filter((c) => c.stage === 'closed_won').length,
      pipelineValue: data
        .filter((c) => ACTIVE_STAGES.includes(c.stage))
        .reduce((sum, c) => sum + (c.estimatedAnnualValue ?? 0), 0),
    };
  }, [data]);

  if (isLoading) return <LoaderScreen />;

  if (error) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load clients"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
        />
      </Card>
    );
  }

  async function onConfirmDelete() {
    if (!deleting) return;
    try {
      await deleteClient.mutateAsync(deleting.id);
      toast.success(`Deleted ${deleting.name}`);
      setDeleting(null);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Delete failed.');
    }
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <SectionHeader
        kicker={`CRM · ${data?.length ?? 0} accounts`}
        title="Clients & Relationships."
        description="Architects, GCs, dealers and developers across the Western U.S., Canada and Mexico."
        action={canWrite && (
          <Button leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
            New Client
          </Button>
        )}
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <Stat label="Total"           value={String(stats.total)} />
        <Stat label="Active Pipeline" value={String(stats.active)} />
        <Stat label="Closed Won"      value={String(stats.won)} />
        <Stat label="Pipeline Value"  value={stats.pipelineValue ? formatCurrency(stats.pipelineValue) : '—'} accent />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[16rem]">
          <Input
            placeholder="Search by name, email, territory…"
            leftIcon={<Search size={14} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="w-44">
          <Select
            value={stage}
            onChange={(e) => setStage(e.target.value as PipelineStage | '')}
            options={[
              { value: '', label: 'All stages' },
              ...PIPELINE_STAGES.map((s) => ({ value: s, label: PIPELINE_LABELS[s] })),
            ]}
          />
        </div>

        <div className="w-44">
          <Select
            value={type}
            onChange={(e) => setType(e.target.value as ClientType | '')}
            options={[
              { value: '', label: 'All types' },
              ...Object.entries(CLIENT_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l })),
            ]}
          />
        </div>

        {/* View switcher */}
        <div className="flex items-center bg-bg-subtle border border-border rounded-sm p-0.5">
          <ViewBtn active={view === 'list'} onClick={() => setView('list')} icon={LayoutList} label="List" />
          <ViewBtn active={view === 'pipeline'} onClick={() => setView('pipeline')} icon={Columns3} label="Pipeline" />
        </div>
      </div>

      {/* Body */}
      {filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon={Building2}
            title={search || stage || type ? 'No clients match your filters' : 'No clients yet'}
            description={search || stage || type
              ? 'Try clearing some filters.'
              : 'Add your first client to start building the pipeline.'}
            action={canWrite && !search && !stage && !type && (
              <Button leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
                New Client
              </Button>
            )}
          />
        </Card>
      ) : view === 'list' ? (
        <ClientsTableView
          clients={filtered}
          canWrite={canWrite}
          onEdit={(c) => setEditing(c)}
          onDelete={(c) => setDeleting(c)}
        />
      ) : (
        <PipelineView clients={filtered} />
      )}

      <ClientFormModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <ClientFormModal open={!!editing} onClose={() => setEditing(null)} client={editing} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={onConfirmDelete}
        loading={deleteClient.isPending}
        destructive
        title={`Delete ${deleting?.name}?`}
        confirmLabel="Delete client"
        description={
          <>
            This permanently removes <span className="text-text">{deleting?.name}</span> along with all contacts
            and interaction history. This action cannot be undone.
          </>
        }
      />
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="kicker mb-2">{label}</div>
      <div className={`font-display text-2xl tabular ${accent ? 'text-brass-300' : 'text-text'}`}>{value}</div>
    </div>
  );
}

function ViewBtn({
  active, onClick, icon: Icon, label,
}: { active: boolean; onClick: () => void; icon: typeof LayoutList; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`
        h-8 px-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] rounded-sm transition-colors
        ${active ? 'bg-bg-panel text-text' : 'text-text-muted hover:text-text'}
      `}
    >
      <Icon size={13} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
